import { message } from 'antd';
import { useContext, useEffect, useRef, useState } from 'react';

import { UserIdentity } from '@/components/RightProvider/const';
import { RightContext } from '@/components/RightProvider/context';
import { LimitConfig } from '@/components/RightProvider/type';
import { cancelUserCoupon } from '@/components/UIComponents/UserAccount/service';
import { UseTranslateType } from '@/hooks/useTranslate';
import UserStore, { DistributionSpecificCouponInfo, IDENTITYKEY } from '@/store/User';
import { VIPWEIGHT } from '@/store/User/const';
import { AIFunction } from '@/utils/const';
import { isCnArea, isFWebSite, isUsArea } from '@/utils/envHelper';
import {
  EventNewID,
  fc_purchase_alert_expose,
  GoogleEventID,
  StatusEnum,
} from '@/utils/eventlog/eventNewConfig';
import { trackEvent, trackGoogleEvent } from '@/utils/eventlog/logAdapter';

import { ContactUsModal } from '../../..';
import { TabActiveKey, TabContentProps } from '..';
import { PayQRCodeShow } from '../components/qrcode';
import { BWebsiteEmail, FWebsiteEmail, getPlanTag } from '../utils';
import { channelPolling } from './channelPay';
import { stripePay, stripePolling } from './stripePay';

type DateType = Extract<TabActiveKey, 'month' | 'year' | 'twoweek'>;
// 支付时保留一下对应的信息
export type OrderInfo = {
  orderId: string;
  orderType: 'combo' | '';
  orderCommodityCode: string;
};
interface UsePaymentProps {
  t: UseTranslateType | undefined;
  defaultActiveTab?: TabActiveKey;
  feature: fc_purchase_alert_expose['from'];
}
interface HandlePayProps {
  key: UserIdentity;
  item: LimitConfig[keyof LimitConfig];
  feature: fc_purchase_alert_expose['from'];
  btnIndex: number;
  price: string;
  currency: string;
  // 新增：当前选择的商品，用于多商品情况
  selectedCommodityCode?: string;
}

function getMaxDisCount(limitConfig: LimitConfig) {
  const getMaxPrice = (data: any[]): number => {
    if (!data || !Array.isArray(data) || data.length === 0) return 0;
    return Math.max(...data.map((item) => item?.realPrice || 0));
  };
  const calculateDiscount = (prices: any): number => {
    const { month, year } = prices?.commodityData || {};

    const monthPrice = getMaxPrice(month);
    const yearPrice = getMaxPrice(year);

    // 避免除以0
    return monthPrice ? Math.floor((1 - yearPrice / 12 / monthPrice) * 100) : 0;
  };
  try {
    if (!limitConfig.advancedVip) {
      return false;
    }
    const advancedDiscount = limitConfig.advancedVip
      ? calculateDiscount(limitConfig.advancedVip)
      : 0;
    return {
      type: 'advancedVip' as IDENTITYKEY,
      disCount: advancedDiscount,
    };
  } catch (error) {
    return false;
  }
}
function EnterpriseCase(feature: fc_purchase_alert_expose['from']) {
  if (isFWebSite) {
    window.location.href = 'mailto:' + FWebsiteEmail;
  } else if (isUsArea) {
    window.location.href = 'mailto:' + BWebsiteEmail;
  } else {
    if (isCnArea) {
      window.open('https://www.wjx.cn/vm/YEMhJVr.aspx#');
    } else {
      ContactUsModal.show();
    }
  }
}

const usePayment = ({ t, defaultActiveTab, feature }: UsePaymentProps) => {
  // 标识是否支付完成，轮训到支付成功后设置为true
  const [isPurchaseSuccess, setIsPurchaseSuccess] = useState(false);
  //埋点传参用
  const featureRef = useRef<fc_purchase_alert_expose['from']>(feature);
  // 优惠信息
  const couponInfo = UserStore((store) => store.couponInfo);
  const [activeTab, setActiveTab] = useState<TabContentProps['activeKey']>(
    defaultActiveTab || 'year',
  );
  const [highlightBenefit, setHighlightBenefit] = useState<{
    type: IDENTITYKEY;
    disCount: number;
  }>({
    type: 'advancedVip',
    disCount: 0,
  });
  const { limitConfig, singlePurchaseConfig, currentVip, withUpdate } =
    useContext(RightContext);
  const weightData = Object.values(limitConfig).map((item) => item.weight);
  const formatData = Object.entries(limitConfig).sort(
    ([, a], [, b]) => ((a as any).weight || 0) - ((b as any).weight || 0),
  ) as [keyof LimitConfig, LimitConfig[keyof LimitConfig]][];

  // activeTabTracking 用于埋点
  const activeTabTracking = useRef<TabContentProps['activeKey']>(activeTab);
  const activeComboTracking = useRef<IDENTITYKEY>('advancedVip');

  const [loadingStates, setLoadingStates] = useState<Array<boolean>>(
    formatData.map(() => false),
  );

  const pollPayKey = 'pollPayKey';
  const orderInfo = useRef<OrderInfo>({
    orderId: '',
    orderType: '',
    orderCommodityCode: '',
  });
  // 该订单使用的优惠券id，用于轮训时的埋点
  const orderCouponCode = useRef('');
  let timer = useRef<NodeJS.Timeout | null>(null);

  // 用于销毁二维码弹窗
  let QRCodeDestroyHandler = useRef();

  const setBtnLoading = (index: number, bool: boolean) => {
    const newLoadingStates = [...loadingStates];
    newLoadingStates[index] = bool;
    setLoadingStates(newLoadingStates);
  };

  const handleTabChange = (data: TabActiveKey) => {
    setActiveTab(data);
  };

  const channelPayCallback = {
    purchase: {
      success: (orderId: string) => {
        if (!isCnArea) {
          message.open({
            key: pollPayKey,
            type: 'loading',
            content: t && t('merror.sub.purchasing'),
            duration: 0,
          });
        }
        orderInfo.current.orderId = orderId;
        // 支付发起成功，开始轮训
        channelPolling({
          orderInfo: orderInfo.current,
          t,
          pollPayKey: 'pollPayKey',
          timer,
          callback: channelPayCallback.polling,
        });
      },
      error: (msg: string) => {
        trackEvent(EventNewID.SUBSCRIPTION_POP_RESULT, {
          source: featureRef.current,
          plan: getPlanTag(activeComboTracking.current, activeTabTracking.current),
          result: StatusEnum.FAIL,
        });
        const couponInfo = UserStore.getState().couponInfo;
        if (couponInfo.type === 'ticket') {
          let availabled = couponInfo.availabled;
          if (availabled?.length) {
            const result = availabled.find((item) =>
              item.applicableGoods.includes(orderInfo.current.orderCommodityCode),
            );
            if (result) {
              cancelUserCoupon(result.couponCode);
            }
          }
        }
        message.open({
          key: pollPayKey,
          type: 'error',
          content: msg || (t && t('merror.sub.purchasefail')),
          duration: 2,
        });
      },
    },
    polling: {
      success: async () => {
        // 获取当前选择的商品数据数组
        const skuDataArray =
          limitConfig?.[activeComboTracking.current]?.commodityData?.[
            activeTabTracking.current as DateType
          ];

        // 使用订单匹配的商品数据，如果找不到则使用第一个
        const skuData =
          skuDataArray?.find(
            (item) => item.commodityCode === orderInfo.current.orderCommodityCode,
          ) || skuDataArray?.[0];

        setIsPurchaseSuccess(true);

        trackGoogleEvent(GoogleEventID.Purchase, {
          transaction_id: `stripe_${orderInfo.current.orderId}`,
          value: skuData?.realPrice,
          currency: skuData?.content?.currency,
        });

        trackEvent(EventNewID.SUBSCRIPTION_POP_RESULT, {
          source: featureRef.current,
          plan: getPlanTag(activeComboTracking.current, activeTabTracking.current),
          result: StatusEnum.SUCCESS,
        });

        message.open({
          key: pollPayKey,
          type: 'success',
          content: t && t('merror.sub.purchasesuc'),
          duration: 2,
        });
        if (isCnArea) {
          // @ts-ignore
          // 关闭二维码弹窗
          QRCodeDestroyHandler?.current?.();
          // 清空保存的优惠券id
          orderCouponCode.current = '';
        }
        localStorage.setItem('userPurchaseFlag', 'true');
        await withUpdate(() => {
          UserStore.getState().setShowNewUserCountDownBaner(false);
        }, false);
      },
      error: (msg: 'timeout' | string) => {
        if (msg === 'timeout') {
          trackEvent(EventNewID.SUBSCRIPTION_POP_RESULT, {
            source: featureRef.current,
            plan: getPlanTag(activeComboTracking.current, activeTabTracking.current),
            result: StatusEnum.FAIL,
          });
          message.open({
            key: pollPayKey,
            type: 'error',
            content: t && t('merror.sub.longtime'),
            duration: 2,
          });
        }
      },
    },
  };
  const stripePayCallback = {
    purchase: {
      success: (orderId: string) => {
        if (!isCnArea) {
          message.open({
            key: pollPayKey,
            type: 'loading',
            content: t && t('merror.sub.purchasing'),
            duration: 0,
          });
        }

        orderInfo.current.orderId = orderId;
        // 支付发起成功，开始轮训
        stripePolling({
          orderInfo: orderInfo.current,
          t,
          pollPayKey: 'pollPayKey',
          timer,
          callback: stripePayCallback.polling,
        });
      },
      error: (msg: string) => {
        message.error(msg);
        trackEvent(EventNewID.SUBSCRIPTION_POP_RESULT, {
          source: featureRef.current,
          plan: getPlanTag(activeComboTracking.current, activeTabTracking.current),
          result: StatusEnum.FAIL,
        });
      },
    },
    polling: {
      success: async () => {
        // 获取当前选择的商品数据数组
        const skuDataArray =
          limitConfig?.[activeComboTracking.current]?.commodityData?.[
            activeTabTracking.current as DateType
          ];

        // 使用订单匹配的商品数据，如果找不到则使用第一个
        const skuData =
          skuDataArray?.find(
            (item) => item.commodityCode === orderInfo.current.orderCommodityCode,
          ) || skuDataArray?.[0];
        setIsPurchaseSuccess(true);

        trackGoogleEvent(GoogleEventID.Purchase, {
          transaction_id: `stripe_${orderInfo.current.orderId}`,
          value: skuData?.realPrice,
          currency: skuData?.content?.currency,
        });

        trackEvent(EventNewID.SUBSCRIPTION_POP_RESULT, {
          source: featureRef.current,
          plan: getPlanTag(activeComboTracking.current, activeTabTracking.current),
          result: StatusEnum.SUCCESS,
        });

        message.open({
          key: pollPayKey,
          type: 'success',
          content: t && t('merror.sub.purchasesuc'),
          duration: 2,
        });
        // 清空保存的优惠券id
        orderCouponCode.current = '';
        localStorage.setItem('userPurchaseFlag', 'true');
        await withUpdate(() => {
          UserStore.getState().setShowNewUserCountDownBaner(false);
        }, false);
      },
      error: (msg: 'timeout' | string) => {
        if (msg === 'timeout') {
          trackEvent(EventNewID.SUBSCRIPTION_POP_RESULT, {
            source: featureRef.current,
            plan: getPlanTag(activeComboTracking.current, activeTabTracking.current),
            result: StatusEnum.FAIL,
          });
          message.open({
            key: pollPayKey,
            type: 'error',
            content: t && t('merror.sub.longtime'),
            duration: 2,
          });
        }
      },
    },
  };

  const handlePay = async ({
    key,
    item,
    btnIndex,
    feature = AIFunction.Other,
    price,
    currency,
    selectedCommodityCode = '',
  }: HandlePayProps) => {
    if (loadingStates.some((state) => state)) return;
    setIsPurchaseSuccess(false);
    featureRef.current = feature;
    activeComboTracking.current = key;

    // 企业版特殊处理
    if (key === 'enterpriseVip') {
      EnterpriseCase(feature);
      return;
    }

    const plan = getPlanTag(activeComboTracking.current, activeTabTracking.current);

    trackGoogleEvent(GoogleEventID.BeginCheckout, {
      subscription_plan: plan,
    });

    trackEvent(EventNewID.SUBSCRIPTION_POP_CLICK, {
      source: feature,
      plan,
    });

    if (!item.commodityData?.month?.length && !item.commodityData?.year?.length) {
      return;
    }

    if (
      currentVip.weight === item.weight &&
      currentVip.weight < VIPWEIGHT.enterpriseVip
    ) {
      // if (currentVip.title === 'year') {
      //   return;
      // }
    }
    if (currentVip.key === 'freeVip' && key === 'freeVip') {
      return;
    }
    if (!isCnArea) {
      setBtnLoading(btnIndex, true);
    }

    // 获取商品数据数组
    const commodityDataArray = item.commodityData[activeTab as DateType];

    // 获取选定的商品
    let commodityCode = '';
    if (selectedCommodityCode && Array.isArray(commodityDataArray)) {
      // 如果提供了商品编码且它存在于商品数组中，则使用它
      if (
        commodityDataArray.some((item) => item.commodityCode === selectedCommodityCode)
      ) {
        commodityCode = selectedCommodityCode;
      }
    }

    // 如果没有提供有效的商品编码，则使用第一个商品的编码
    if (
      !commodityCode &&
      Array.isArray(commodityDataArray) &&
      commodityDataArray.length > 0
    ) {
      commodityCode = commodityDataArray[0].commodityCode;
    }

    if (!commodityCode) {
      console.error('商品code不存在');
      return;
    }

    orderInfo.current = {
      orderId: '',
      orderType: 'combo',
      orderCommodityCode: commodityCode,
    };
    try {
      let couponCode = '';
      const couponInfo = UserStore.getState().couponInfo;
      if (couponInfo.type === 'ticket') {
        let availabled = couponInfo.availabled;
        if (availabled?.length) {
          const result = availabled.find((item) =>
            item.applicableGoods.includes(commodityCode),
          );
          if (result) {
            couponCode = result.couponCode;
            orderCouponCode.current = couponCode;
          }
        }
      }
      if (isCnArea) {
        PayQRCodeShow({
          destroyHandler: QRCodeDestroyHandler,
          feature,
          commodityCode,
          couponCode,
          price,
          currency,
          callback: channelPayCallback.purchase,
          onClose: () => {
            // 关闭轮训message提示
            message.destroy(pollPayKey);
            timer.current && clearTimeout(timer.current);
          },
          onTabChange(orderId) {
            // tab切换之后，更新orderId，重置轮训
            orderInfo.current.orderId = orderId;
            timer.current && clearTimeout(timer.current);
            channelPolling({
              orderInfo: orderInfo.current,
              t,
              pollPayKey: 'pollPayKey',
              timer,
              callback: channelPayCallback.polling,
            });
          },
        });
      } else {
        await stripePay({
          couponCode,
          feature: featureRef.current,
          commodityCode: String(commodityCode),
          type: 'combo',
          callback: stripePayCallback.purchase,
        });
      }
    } catch (error) {
      message.open({
        key: pollPayKey,
        type: 'error',
        content: (error as any).message || (t && t('merror.sub.purchasefail')),
        duration: 2,
      });
    } finally {
      if (!isCnArea) {
        setBtnLoading(btnIndex, false);
      }
    }
  };

  useEffect(() => {
    // 有优惠券的话高亮其tab
    if (!limitConfig) {
      return;
    }
    const ticketHandle = couponInfo.type === 'ticket' && couponInfo.availabled;
    const specificHandle =
      couponInfo.type === 'distribution_specific' &&
      couponInfo?.data?.commodityDiscount?.length;
    if ((ticketHandle || specificHandle) && limitConfig) {
      let tabType = 'year' as TabActiveKey;
      Object.keys(limitConfig).some((vip) => {
        const monthDataArray =
          limitConfig[vip as UserIdentity]?.commodityData?.month || [];
        const yearDataArray = limitConfig[vip as UserIdentity]?.commodityData?.year || [];
        const twoweekDataArray =
          limitConfig[vip as UserIdentity]?.commodityData?.twoweek || [];

        const applicableGoods = ticketHandle
          ? couponInfo.availabled?.map((item) => item.applicableGoods).flat()
          : (couponInfo as DistributionSpecificCouponInfo)?.data?.commodityDiscount?.map(
              (item) => item.commodityCode,
            );

        // 检查月度商品数组中是否有匹配的商品码
        const hasMatchingMonthCommodity =
          Array.isArray(monthDataArray) &&
          monthDataArray.some((item) => {
            if (Array.isArray(applicableGoods)) {
              return applicableGoods.includes(item.commodityCode);
            }
            return item.commodityCode === applicableGoods;
          });

        // 检查年度商品数组中是否有匹配的商品码
        const hasMatchingYearCommodity =
          Array.isArray(yearDataArray) &&
          yearDataArray.some((item) => {
            {
              if (Array.isArray(applicableGoods)) {
                return applicableGoods.includes(item.commodityCode);
              }
              return item.commodityCode === applicableGoods;
            }
          });
        const hasMatchingTwoweekCommodity =
          Array.isArray(twoweekDataArray) &&
          twoweekDataArray.some((item) => {
            {
              if (Array.isArray(applicableGoods)) {
                return applicableGoods.includes(item.commodityCode);
              }
              return item.commodityCode === applicableGoods;
            }
          });

        if (hasMatchingMonthCommodity) {
          tabType = 'month';
          return true;
        }

        if (hasMatchingYearCommodity) {
          tabType = 'year';
          return true;
        }
        if (hasMatchingTwoweekCommodity) {
          tabType = 'twoweek';
          return true;
        }

        return (
          hasMatchingMonthCommodity ||
          hasMatchingYearCommodity ||
          hasMatchingTwoweekCommodity
        );
      });
      setActiveTab(tabType);
    }
  }, [limitConfig, couponInfo]);

  useEffect(() => {
    // 获取要高亮的套餐，计算折扣价格
    const maxDiscount = getMaxDisCount(limitConfig);
    if (maxDiscount) {
      setHighlightBenefit(maxDiscount);
    }
  }, [limitConfig]);

  useEffect(() => {
    // 监听用户是否切换标签页，用于支付结果的弹窗提示
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible') {
        // 用户回到此标签页
        try {
          // 防止没有发起订单就去轮训
          if (!orderInfo.current.orderId && !orderInfo.current.orderType) {
            return;
          }

          console.log('stripe轮训');
          await stripePolling({
            orderInfo: orderInfo.current,
            t,
            pollPayKey,
            timer,
            callback: stripePayCallback.polling,
          });

          await withUpdate(() => {}, false);
        } catch (error) {
          console.error(error);
        }
      } else {
        // 用户离开此标签页
        timer.current && clearTimeout(timer.current);
      }
    };

    if (!isCnArea) {
      // 国内不需要监听
      document.addEventListener('visibilitychange', handleVisibilityChange);
    }
    // 清理函数，移除事件监听器
    return () => {
      timer.current && clearTimeout(timer.current);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [limitConfig, singlePurchaseConfig]);

  useEffect(() => {
    activeTabTracking.current = activeTab;
    setLoadingStates(loadingStates.map(() => false));
  }, [activeTab]);

  return {
    handlePay,
    loadingStates,
    activeTab,
    highlightBenefit,
    handleTabChange,
    formatData,
    weightData,
    isPurchaseSuccess,
  };
};
export default usePayment;
